import { Select } from 'antd';
import { useState } from 'react';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { canOfferAdd, type PickerOption } from '../policies/catalogue-options';

const ADD_VALUE = '__add__';

interface AddableSelectProps {
  /** Set by the wrapping Form.Item, so the label points at the input. */
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  options: PickerOption[];
  /** Names the catalogue holds, so what is typed is only offered for adding when it is new (the options may also carry a legacy value). */
  knownNames: string[];
  placeholder?: string;
  disabled?: boolean;
  /** What adding is called in the offer ("make", "model"); absent when the user may not add. Resolves to the name the catalogue stored. */
  add?: { noun: string; run: (name: string) => Promise<string> };
}

/** A searchable list that opens with every choice and, for someone allowed to extend the catalogue, offers to add what was typed. */
export function AddableSelect({ id, value, onChange, options, knownNames, placeholder, disabled, add }: AddableSelectProps) {
  const [typed, setTyped] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const addOption =
    add &&
    canOfferAdd(
      typed,
      knownNames.map((name) => ({ value: name, label: name })),
    )
      ? [{ value: ADD_VALUE, label: `Add "${typed.trim()}" as a ${add.noun}` }]
      : [];

  async function choose(chosen: string) {
    if (chosen !== ADD_VALUE || !add) {
      setAddError(null);
      onChange?.(chosen);
      return;
    }
    try {
      onChange?.(await add.run(typed.trim()));
      setAddError(null);
    } catch (err) {
      setAddError(apiErrorMessage(err) ?? `Could not add the ${add.noun}`);
    }
  }

  return (
    <>
      <Select
        id={id}
        value={value}
        onChange={choose}
        showSearch
        disabled={disabled}
        placeholder={placeholder}
        onSearch={setTyped}
        onOpenChange={(open) => open || setTyped('')}
        options={[...options, ...addOption]}
        filterOption={(input, option) =>
          option?.value === ADD_VALUE ||
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.trim().toLowerCase())
        }
        notFoundContent="No match"
      />
      {addError && (
        <p role="alert" className="mt-1 text-xs text-error">
          {addError}
        </p>
      )}
    </>
  );
}
